"use client";

import React from "react";
import { WandSparkles, LayoutTemplate, Mic, Play, Sparkles } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
const features = [{
  title: "Document Automation",
  description: "Streamline Metrc manifests and DocuSign agreements with intelligent workflows that eliminate manual data entry and errors.",
  icon: WandSparkles,
  type: "wand"
}, {
  title: "Compliance Reporting",
  description: "Access a library of state-specific reporting modules designed to keep your dispensary fully audited and compliant 24/7.",
  icon: LayoutTemplate,
  type: "templates"
}, {
  title: "Inventory Analytics",
  description: "Monitor stock velocity and predict demand trends with our AI engine, ensuring you never miss a sale or overstock a shelf.",
  icon: Mic,
  type: "waveform"
}];
const waveformBars = [{
  default: "h-4",
  hover: "group-hover:h-12",
  delay: "delay-0"
}, {
  default: "h-8",
  hover: "group-hover:h-16",
  delay: "delay-75"
}, {
  default: "h-5",
  hover: "group-hover:h-10",
  delay: "delay-100"
}, {
  default: "h-12",
  hover: "group-hover:h-16",
  delay: "delay-150"
}, {
  default: "h-7",
  hover: "group-hover:h-14",
  delay: "delay-200"
}, {
  default: "h-10",
  hover: "group-hover:h-16",
  delay: "delay-300"
}, {
  default: "h-6",
  hover: "group-hover:h-12",
  delay: "delay-500"
}, {
  default: "h-4",
  hover: "group-hover:h-8",
  delay: "delay-700"
}];
export default function TheAiMagicBehindTheLens() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: gridRef,
    isVisible: isGridVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="145611" id="operational-intelligence-features" className="dark bg-background text-foreground py-24 relative overflow-hidden">
      {}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-primary/10 rounded-full blur-[120px] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div ref={headerRef} className={`text-center max-w-3xl mx-auto mb-16 transition-all duration-1000 ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#1A5A8E]/10 text-[#1A5A8E] text-sm font-medium mb-6 border border-[#1A5A8E]/20">
            <Sparkles className="w-4 h-4" />
            <span>Enterprise Intelligence</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-6 text-foreground">
            Operational Visibility <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#1A5A8E] to-[#4092C7]">Behind the Cloud</span>
          </h2>
          <p className="text-lg text-muted-foreground">
            Discover how our Salesforce-integrated platform transforms complex cannabis compliance into a competitive advantage for your retail operation.
          </p>
        </div>

        <div ref={gridRef} className={`grid grid-cols-1 md:grid-cols-3 gap-8 transition-all duration-1000 delay-200 ${isGridVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          {features.map((feature, index) => <Card key={index} data-index={index} className="group relative overflow-hidden bg-card border-x-border border-b-border border-t-2 border-t-[#1A5A8E] rounded-2xl transition-all duration-500 hover:-translate-y-2 hover:shadow-[0_20px_25px_-5px_rgba(26,90,142,0.15)]">
              <CardHeader className="pb-4">
                <div className="w-12 h-12 rounded-xl bg-[#1A5A8E]/10 flex items-center justify-center mb-4 border border-[#1A5A8E]/20 group-hover:bg-[#1A5A8E]/20 transition-colors duration-300">
                  <feature.icon className="w-6 h-6 text-[#1A5A8E]" />
                </div>
                <CardTitle className="text-xl font-semibold text-card-foreground">
                  {feature.title}
                </CardTitle>
              </CardHeader>
              <CardContent className="flex flex-col justify-between h-[calc(100%-100px)]">
                <CardDescription className="text-muted-foreground text-base mb-8">
                  {feature.description}
                </CardDescription>

                {}
                <div className="mt-auto h-32 flex items-end justify-center pb-4 relative">
                  
                  {feature.type === "wand" && <div className="relative flex items-center justify-center w-full h-full">
                      <div className="absolute inset-0 bg-[#1A5A8E]/5 rounded-full blur-xl group-hover:bg-[#1A5A8E]/20 transition-all duration-500" />
                      <WandSparkles className="w-16 h-16 text-[#1A5A8E]/80 group-hover:text-[#1A5A8E] transform group-hover:rotate-[25deg] group-hover:scale-125 transition-all duration-500 ease-out drop-shadow-[0_0_15px_rgba(26,90,142,0.4)]" />
                      <Sparkles className="absolute top-4 right-1/4 w-6 h-6 text-[#4092C7] opacity-0 group-hover:opacity-100 group-hover:-translate-y-4 group-hover:translate-x-4 transition-all duration-700 delay-100" />
                      <Sparkles className="absolute bottom-4 left-1/4 w-4 h-4 text-[#1A5A8E] opacity-0 group-hover:opacity-100 group-hover:translate-y-4 group-hover:-translate-x-4 transition-all duration-700 delay-200" />
                    </div>}

                  {feature.type === "templates" && <div className="relative w-full h-full flex items-center justify-center">
                      <div className="absolute w-20 h-24 bg-muted rounded-lg border border-border/50 rotate-12 translate-x-6 translate-y-2 opacity-30 group-hover:rotate-[20deg] group-hover:translate-x-10 transition-all duration-500 ease-out" />
                      <div className="absolute w-20 h-24 bg-muted/80 rounded-lg border border-border/80 rotate-6 translate-x-3 translate-y-1 opacity-60 group-hover:rotate-[10deg] group-hover:translate-x-5 transition-all duration-500 ease-out delay-75" />
                      <div className="relative w-20 h-24 bg-background rounded-lg border-2 border-[#1A5A8E] z-10 flex items-center justify-center group-hover:-rotate-3 group-hover:scale-105 transition-all duration-500 ease-out shadow-lg shadow-[#1A5A8E]/20">
                        <Play className="w-8 h-8 text-[#1A5A8E] ml-1 opacity-80 group-hover:opacity-100 transition-opacity" />
                      </div>
                    </div>}

                  {feature.type === "waveform" && <div className="flex items-end justify-center gap-1.5 w-full h-20">
                      {waveformBars.map((bar, i) => <div key={i} data-index={i} className={`w-2.5 bg-[#1A5A8E]/80 group-hover:bg-[#1A5A8E] rounded-full transition-all duration-500 ease-in-out ${bar.default} ${bar.hover} ${bar.delay}`} style={{
                  transformOrigin: "bottom"
                }} />)}
                    </div>}

                </div>
              </CardContent>
            </Card>)}
        </div>
      </div>
    </section>;
}